import axios from 'axios';
import { TrainingStatus, TrainingType } from '../types/training';

const MOODLE_PROXY_URL = '/api/moodleProxy';

/**
 * Helper to call the backend Moodle proxy API.
 */
const callMoodle = async (action: string, params: any = {}) => {
    try {
        const response = await axios.post(MOODLE_PROXY_URL, { action, params });

        return response.data;
    } catch (error: any) {
        const message = error.response?.data?.error || error.message || 'Moodle proxy request failed';
        console.error(`❌ Moodle API Call Failed (${action}):`, message);
        throw new Error(message);
    }
};

/**
 * Synchronize Portal user with Moodle
 */
export const syncMoodleUser = async (email: string, firstName: string, lastName: string) => {
    return await callMoodle('syncMoodleUser', {
        email,
        firstname: firstName,
        lastname: lastName
    });
};

/**
 * Create a new training program in Moodle
 */
export const createMoodleProgram = async (data: any) => {
    return await callMoodle('createMoodleProgram', {
        trainingid: data.trainingId || data.TrainingID,
        fullname: data.trainingName || data.TrainingName,
        passingpercentage: data.passingPercentage ?? data.PassingPercentage, // 0 = no cutoff, keep it
        estimatedefforthours: data.estimatedEffortHours || data.EstimatedEffortHours,
        trainingtype: data.trainingType || data.TrainingType,
        audienceemails: data.audienceEmails || data.AudienceEmails,
        stakeholderemails: data.stakeholderEmails || data.StakeholderEmails,
        traininglink: data.trainingLink || data.TrainingLink,
        startdate: data.startdate || 0,
        enddate: data.enddate || 0,
        startDate: data.startDate || '',
        endDate: data.endDate || '',
        programtype: data.programType || data.programtype || 'AI',
        traineremails: data.trainerEmails || data.traineremails || data.TrainerEmails || '',
        visible: data.visible !== undefined ? data.visible : 1,
        createdby: data.createdby || '',
        assessments: data.assessments || '',
        discussionenabled: data.discussionEnabled !== undefined ? (data.discussionEnabled ? 1 : 0) : 1,
    });
};

export const publishMoodleProgram = async (courseId: number) => {
    return await callMoodle('publishMoodleProgram', { courseid: courseId });
};

/**
 * Create a Moodle quiz within a course
 */
export const createMoodleQuiz = async (courseId: number, name: string, intro: string, sectionNum: number = 0, questions: any[] = [], timeLimit: number = 30, attemptLimit: number = 0, timeClose: number = 0) => {
    return await callMoodle('createMoodleQuiz', {
        courseid: courseId,
        name,
        intro,
        sectionnum: sectionNum,
        questions: JSON.stringify(questions),
        timelimit: timeLimit,
        attemptlimit: attemptLimit,
        timeclose: timeClose
    });
};

/**
 * Get questions and ref answers for a specific quiz
 */
export const getMoodleQuizQuestions = async (courseId: number, quizName: string, quizId: number = 0): Promise<{ questions: any[]; quizId: number }> => {
    const response = await callMoodle('getMoodleQuizQuestions', {
        courseid: courseId,
        ...(quizId > 0 ? { quizid: quizId } : { quizname: quizName })
    });
    return { questions: response.questions || [], quizId: response.quizid || 0 };
};

/**
 * Delete the Moodle quiz behind a deleted AI-led module (with its attempts and
 * grades). Completion counts every quiz in the course, so leaving it behind
 * would stop participants from completing the program. quizId is preferred;
 * quizName is the fallback for modules saved before quiz ids were stored.
 */
export const deleteMoodleQuiz = async (courseId: number, quizName: string, quizId: number = 0): Promise<{ status: string; deleted: number }> => {
    return await callMoodle('deleteMoodleQuiz', { courseid: courseId, quizid: quizId, quizname: quizName });
};

export const updateMoodleQuizQuestions = async (courseId: number, quizName: string, questions: any[], quizId: number = 0, timeClose: number = -1, attemptLimit: number = -1, timeLimit: number = -1, newName?: string) => {
    return await callMoodle('updateMoodleQuizQuestions', {
        courseid: courseId,
        ...(quizId > 0 ? { quizid: quizId } : { quizname: quizName }),
        questions: JSON.stringify(questions),
        ...(timeClose >= 0 ? { timeclose: timeClose } : {}),
        ...(attemptLimit >= 0 ? { attemptlimit: attemptLimit } : {}),
        ...(timeLimit >= 0 ? { timelimit: timeLimit } : {}),
        ...(newName && newName !== quizName ? { newname: newName } : {})
    });
};

/**
 * Helper to map Moodle API response to Portal's Training/TrainingMaster interfaces
 * Supports both camelCase (User UI) and PascalCase (Admin UI/Legacy)
 */
const mapMoodleProgram = (p: any) => ({
    id: p.courseid || p.id,
    courseId: p.courseid || p.id,
    trainingId: p.trainingid || '',
    trainingName: p.name,
    status: p.status || TrainingStatus.NOT_STARTED,
    assignedDate: new Date().toISOString(),
    active: !!p.visible,
    passingPercentage: p.passingpercentage ?? 80,
    estimatedEffortHours: p.estimatedefforthours || 0,
    startDate: p.startdate ? new Date(p.startdate * 1000).toISOString() : '',
    endDate: p.enddate && p.enddate > 0 ? new Date(p.enddate * 1000).toISOString() : '',
    trainingType: p.type || p.trainingtype || TrainingType.OPEN,
    catalogVisible: (p.type || p.trainingtype) !== TrainingType.PRIVATE,
    audienceEmails: p.audienceemails || '',
    stakeholderEmails: p.stakeholderemails || '',
    trainingLink: p.traininglink || '',
    assessments: p.assessments || '',
    percentage: p.percentage || 0,
    visible: !!p.visible,
    isMandatory: !!p.is_mandatory,
    isTrainer: !!p.is_trainer,
    isStakeholder: !!p.is_stakeholder,
    trainerEmails: p.traineremails || '',
    programType: p.programtype || 'AI',
    ispublished: p.ispublished !== undefined ? !!p.ispublished : true,
    createdBy: p.createdby || '',
    discussionEnabled: p.discussionenabled !== undefined ? !!p.discussionenabled : true,
});

/**
 * Get all training programs from Moodle
 */
export const getMoodlePrograms = async (email?: string) => {
    const response = await callMoodle('getMoodlePrograms', { email });
    return (response.programs || []).map(mapMoodleProgram);
};

/**
 * Get program details by TrainingID or CourseID
 */
export const getMoodleProgramDetails = async (trainingId: string = '', courseId: number = 0) => {
    const p = await callMoodle('getMoodleProgramDetails', {
        trainingid: trainingId,
        courseid: courseId
    });
    return mapMoodleProgram(p);
};

/**
 * Update an existing training program in Moodle
 */
export const updateMoodleProgram = async (data: any) => {
    return await callMoodle('updateMoodleProgram', {
        courseid: data.id,
        fullname: data.trainingName || data.TrainingName,
        passingpercentage: data.passingPercentage ?? data.PassingPercentage, // 0 = no cutoff, keep it
        estimatedefforthours: data.estimatedEffortHours || data.EstimatedEffortHours,
        trainingtype: data.trainingType || data.TrainingType,
        audienceemails: data.audienceEmails || data.AudienceEmails || '',
        stakeholderemails: data.stakeholderEmails || data.StakeholderEmails || '',
        traininglink: data.trainingLink || data.TrainingLink || '',
        startdate: data.startdate || (data.startDate ? Math.floor(new Date(data.startDate).getTime() / 1000) : 0),
        enddate: data.enddate || (data.endDate ? Math.floor(new Date(data.endDate).getTime() / 1000) : 0),
        programtype: data.programType || data.programtype || 'AI',
        traineremails: data.trainerEmails || data.traineremails || data.TrainerEmails || '',
        assessments: data.assessments || '',
        skipupdatenotification: 1,
        discussionenabled: data.discussionEnabled !== undefined ? (data.discussionEnabled ? 1 : 0) : 1,
    });
};

export const sendProgramUpdateNotification = async (courseId: number, changeSummary: string, modifiedBy?: string) => {
    return await callMoodle('sendProgramUpdateNotification', {
        courseid: courseId,
        change_summary: changeSummary,
        modifiedby: modifiedBy || '',
    });
};

/**
 * Delete a training program from Moodle
 */
export const deleteMoodleProgram = async (courseId: number) => {
    return await callMoodle('deleteMoodleProgram', {
        courseid: courseId
    });
};

/**
 * Toggle training program visibility (Active/Inactive)
 */
export const toggleMoodleProgramStatus = async (courseId: number, visible: boolean) => {
    return await callMoodle('toggleMoodleProgramStatus', {
        courseid: courseId,
        visible: visible ? 1 : 0
    });
};

/**
 * Enroll a user in a Moodle course
 */
export const enrollMoodleUser = async (courseId: number, email: string, role: string = 'student') => {
    return await callMoodle('enrollMoodleUser', {
        courseid: courseId,
        email: email,
        role: role
    });
};

/**
 * Unenroll a user from a Moodle course
 */
export const unenrollMoodleUser = async (courseId: number, email: string) => {
    return await callMoodle('unenrollMoodleUser', {
        courseid: courseId,
        email: email
    });
};

/**
 * Get pending enrollment requests
 */
export const getEnrollmentRequests = async () => {
    const response = await callMoodle('getEnrollmentRequests');
    // Handle both direct array and nested 'requests' property for robustness
    return Array.isArray(response) ? response : (response.requests || []);
};

/**
 * Approve an enrollment request
 */
export const approveEnrollmentRequest = async (requestId: number) => {
    return await callMoodle('approveEnrollment', { requestid: requestId });
};

/**
 * Reject an enrollment request
 */
export const rejectEnrollmentRequest = async (requestId: number) => {
    return await callMoodle('rejectEnrollment', { requestid: requestId });
};

/**
 * Add a manual module (Assignment) to a Trainer-Led program
 */
export const addManualModule = async (data: { courseid: number | string, title: string, content: string, deadline: number, sectionnum?: number, grade?: number, sectionname?: string, statedeadlines?: string, workflowenabled?: number, evalcriteria?: string }) => {
    return await callMoodle('addManualModule', data);
};

/**
 * Update an existing manual module (Assignment)
 */
export const updateManualModule = async (data: { assignmentid: number | string, title?: string, content?: string, deadline?: number, grade?: number, sectionname?: string, statedeadlines?: string, workflowenabled?: number, evalcriteria?: string, extractedquestions?: string }) => {
    return await callMoodle('updateManualModule', data);
};

/**
 * Delete a manual module (Assignment)
 */
export const deleteManualModule = async (assignmentId: number | string) => {
    return await callMoodle('deleteManualModule', { assignmentid: assignmentId });
};

/**
 * Get manual modules and submission status for a Trainer-Led program
 */
export const getManualModules = async (courseId: number | string, userEmail?: string | number) => {
    const response = await callMoodle('getManualModules', { courseid: courseId, userid: userEmail });
    if (response && response.modules) {
        // Map Moodle numeric 'id' to 'moduleId' for frontend compatibility
        response.modules = response.modules.map((m: any) => ({
            ...m,
            moduleId: String(m.id)
        }));
    }
    return response;
};

/**
 * Submit an answer for a manual module
 */
export const submitManualAnswer = async (data: { assignmentid: number | string, userid: string | number, answer: string }) => {
    return await callMoodle('submitManualAnswer', data);
};

/**
 * Add a comment to a manual submission
 */
export const addManualComment = async (params: { 
    submissionid: number, 
    assignmentid: number, 
    userid: string | number, 
    comment: string,
    targetuserid?: string 
}) => {
    return await callMoodle('addManualComment', { 
        submissionid: params.submissionid, 
        userid: params.userid, 
        comment: params.comment,
        assignmentid: params.assignmentid,
        targetuserid: params.targetuserid || ''
    });
};

/**
 * Get comments for a manual submission
 */
export const getManualComments = async (submissionId: number | string) => {
    const response = await callMoodle('getManualComments', { submissionid: submissionId });
    return response.comments || [];
};

export const updateAssignmentState = async (data: { assignmentid: number | string, userid: string | number, newstate: string, changedby: string, note?: string }) => {
    return await callMoodle('updateAssignmentState', data);
};

/**
 * A trainer starts reviewing a participant's submission: the participant then
 * sees "Under review" until a decision or a new submission. No email.
 */
export const startReview = async (data: { assignmentid: number | string, userid: string | number, reviewer: string }): Promise<{ reviewby: string; reviewstartedat: number }> => {
    return await callMoodle('startReview', data);
};

export const gradeManualModule = async (assignmentId: number | string, userId: string | number, grade: number) => {
    return await callMoodle('gradeManualModule', { assignmentid: assignmentId, userid: userId, grade });
};

export const getManualCourseProgress = async (courseId: number | string) => {
    const response = await callMoodle('getManualCourseProgress', { courseid: courseId });
    return response || [];
};

export const getAiProgramProgress = async (trainingId: string) => {
    const response = await callMoodle('getAiProgramProgress', { trainingid: trainingId });
    return response as {
        modules: { id: number; name: string }[];
        participants: {
            email: string;
            fullname: string;
            overallstatus: string;
            avgscore: number;
            quizscores: { quizid: number; grade: number; attempts: number; status: string; deadline_override: number }[];
        }[];
    };
};

export const setDeadlineOverride = async (
    trainingId: string, quizId: number, participantEmail: string, newDeadline: number, grantedBy: string
) => {
    return await callMoodle('setDeadlineOverride', {
        trainingid: trainingId, quizid: quizId,
        participantemail: participantEmail, newdeadline: newDeadline, grantedby: grantedBy,
    });
};

export const removeDeadlineOverride = async (trainingId: string, quizId: number, participantEmail: string) => {
    return await callMoodle('removeDeadlineOverride', {
        trainingid: trainingId, quizid: quizId, participantemail: participantEmail,
    });
};

export const getDeadlineOverrides = async (trainingId: string, participantEmail = '') => {
    const response = await callMoodle('getDeadlineOverrides', {
        trainingid: trainingId, participantemail: participantEmail,
    });
    return (response?.overrides || []) as {
        quizid: number; participantemail: string; newdeadline: number; grantedby: string;
    }[];
};

export const getStudentModuleFeedback = async (email: string, courseId: number): Promise<{
    quizid: number;
    whyfailed: string[];
    focusareas: string[];
    score: number;
    timemodified: number;
}[]> => {
    const response = await callMoodle('getStudentModuleFeedback', { email, courseid: courseId });
    return (response.feedback || []).map((fb: any) => ({
        quizid:       fb.quizid,
        whyfailed:    JSON.parse(fb.whyfailed  || '[]'),
        focusareas:   JSON.parse(fb.focusareas || '[]'),
        score:        fb.score,
        timemodified: fb.timemodified,
    }));
};

/**
 * Uploads a file to Azure Blob Storage and registers metadata in Moodle
 */
export const uploadFile = async (data: {
    fileName: string,
    fileContent: string,
    assignmentid: number | string,
    userid?: string | number,
    filearea?: 'intro' | 'submission',
    filetype?: string,
    filesize?: number,
    trainingid?: string
}) => {
    return await callMoodle('uploadBlob', data);
};

/**
 * Gets file metadata for an assignment/module
 */
export const getAssignmentFiles = async (assignmentId: number | string, fileArea: string = '', userEmail?: string) => {
    const response = await callMoodle('getAssignmentFiles', { 
        assignmentid: assignmentId, 
        filearea: fileArea,
        userid: userEmail 
    });
    return response.files || [];
};

/**
 * Deletes file metadata from Moodle
 */
export const deleteFileMetadata = async (fileId: number) => {
    return await callMoodle('deleteFileMetadata', { fileid: fileId });
};

/**
 * Sends a progress report CSV to stakeholder emails via Graph API
 */
export const sendProgressReport = async (data: {
    courseid: number;
    stakeholder_emails: string;
    attachment_b64: string;
    attachment_filename: string;
    program_name: string;
    period?: string;
    participant_count?: number;
    overall_pct?: number;
    trainer_comment?: string;
}) => {
    return await callMoodle('sendProgressReport', data);
};

export const saveTrainerEval = async (assignmentId: number | string, userId: number | string, evaluation: string) => {
    return await callMoodle('saveTrainerEval', { assignmentid: assignmentId, userid: userId, evaluation });
};
